import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Colors } from "../../../assets/Colors";

const API = process.env.REACT_APP_API_URL;
const MAKS_FOTO = 6;

function headers() {
  const user = JSON.parse(localStorage.getItem("user"));
  return { Authorization: "Bearer " + user?.Token, Accept: "*/*" };
}

export const pesanError = (e, cadangan) => e.response?.data?.message || e.response?.data?.Message || cadangan;

// Singkatan jenis servis untuk lencana kecil di kartu room.
export const SINGKAT = { BERSIH: "B", LAUNDRY: "L", MEAL: "M", LAINNYA: "+" };

// Perkecil foto dari kamera HP sebelum diunggah (sisi terpanjang 1600 px, JPEG 0,8).
// Gagal (format tak didukung browser) -> dilempar agar pengguna tahu, bukan diunggah mentah.
export async function kompresFoto(file) {
  if (!file.type.startsWith("image/")) throw new Error(`"${file.name}" bukan gambar`);
  const bmp = await createImageBitmap(file).catch(() => null);
  if (!bmp) throw new Error(`"${file.name}" tidak dapat dibaca sebagai gambar (format tidak didukung)`);
  const skala = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
  const kanvas = document.createElement("canvas");
  kanvas.width = Math.round(bmp.width * skala);
  kanvas.height = Math.round(bmp.height * skala);
  kanvas.getContext("2d").drawImage(bmp, 0, 0, kanvas.width, kanvas.height);
  const blob = await new Promise((res) => kanvas.toBlob(res, "image/jpeg", 0.8));
  if (!blob) throw new Error(`"${file.name}" gagal diproses`);
  return new File([blob], (file.name.replace(/\.[^.]+$/, "") || "foto") + ".jpg", { type: "image/jpeg" });
}

// Foto bukti butuh token, jadi diambil sebagai blob lalu ditampilkan lewat object URL.
export function FotoThumb({ id, onBuka }) {
  const [url, setUrl] = useState(null);
  const [gagal, setGagal] = useState(false);
  useEffect(() => {
    let batal = false;
    let objek = null;
    axios
      .get(API + "getAkomodasiFoto", { headers: headers(), params: { id }, responseType: "blob" })
      .then((res) => {
        if (batal) return;
        objek = URL.createObjectURL(res.data);
        setUrl(objek);
      })
      .catch(() => !batal && setGagal(true));
    return () => {
      batal = true;
      if (objek) URL.revokeObjectURL(objek);
    };
  }, [id]);
  if (gagal) return <div className="w-16 h-16 bg-gray-100 text-[10px] text-gray-500 flex items-center justify-center rounded">gagal</div>;
  if (!url) return <div className="w-16 h-16 bg-gray-100 animate-pulse rounded" />;
  return (
    <img
      src={url}
      alt="Bukti layanan"
      onClick={() => onBuka(url)}
      className="w-16 h-16 object-cover rounded border border-gray-300 cursor-zoom-in"
    />
  );
}

export function Lightbox({ url, onTutup }) {
  if (!url) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={onTutup}>
      <img src={url} alt="Bukti layanan" className="max-w-full max-h-full rounded" />
    </div>
  );
}

// Paket servis satu/banyak room: centang jenis servis yang DIBERIKAN di room itu.
export function PaketServisEditor({ jenis, awal, onSimpan, sibuk, judulTombol = "Simpan Paket Servis" }) {
  const [pilih, setPilih] = useState(awal);
  useEffect(() => setPilih(awal), [awal.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (id) => setPilih((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  return (
    <div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {jenis.map((j) => (
          <label key={j.JenisID} className="flex items-center gap-1 text-sm text-gray-700">
            <input type="checkbox" checked={pilih.includes(j.JenisID)} onChange={() => toggle(j.JenisID)} />
            {j.Nama}
          </label>
        ))}
      </div>
      <button
        type="button"
        disabled={sibuk}
        onClick={() => onSimpan(pilih)}
        style={{ backgroundColor: Colors.primary }}
        className="mt-2 text-white rounded px-3 h-8 text-sm disabled:opacity-60"
      >
        {sibuk ? "Menyimpan..." : judulTombol}
      </button>
    </div>
  );
}

// Modal paket servis untuk banyak room sekaligus (dari seleksi di mode Atur Denah).
export function ModalPaketBanyak({ jumlahRoom, jenis, onSimpan, onTutup, sibuk, pesan }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onTutup}>
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-4" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-base font-semibold text-gray-800 mb-1">Atur servis untuk {jumlahRoom} room</h2>
        <p className="text-xs text-gray-500 mb-3">Paket servis lama di room terpilih akan DIGANTI dengan pilihan ini.</p>
        <PaketServisEditor jenis={jenis} awal={[]} onSimpan={onSimpan} sibuk={sibuk} judulTombol="Terapkan ke Room Terpilih" />
        {pesan && <p className="text-sm text-red-600 mt-2">{pesan}</p>}
        <button type="button" onClick={onTutup} className="mt-3 text-sm text-gray-600 underline">
          Tutup
        </button>
      </div>
    </div>
  );
}

// Bagian "Layanan hari ini" di panel room: daftar catatan + foto, dan form catat layanan.
export function LayananRoom({ room, tanggal, data, onUbah, onBukaFoto }) {
  const [jenisId, setJenisId] = useState("");
  const [catatan, setCatatan] = useState("");
  const [foto, setFoto] = useState([]); // File[] hasil kompresi
  const [preview, setPreview] = useState([]);
  const [pesan, setPesan] = useState("");
  const [sibuk, setSibuk] = useState(false);
  const inputRef = useRef(null);

  // Urutkan: servis yang berpaket di room ini dulu, lalu jenis lain.
  const daftarJenis = useMemo(() => {
    const paket = data.JenisLayanan.filter((j) => room.Layanan.includes(j.JenisID));
    // Paket terkunci (penghuni klien ber-QO): hanya jenis yang ada di QO yang boleh dicatat.
    // Paket terkunci (klien ber-QO) sudah memuat Pembersihan (wajib) + layanan tambahan QO: tak ada jenis lain.
    const lain = room.PaketTerkunci ? [] : data.JenisLayanan.filter((j) => !room.Layanan.includes(j.JenisID));
    return [...paket, ...lain];
  }, [data.JenisLayanan, room.Layanan, room.PaketTerkunci]);
  useEffect(() => {
    if (!jenisId && daftarJenis.length) setJenisId(daftarJenis[0].JenisID);
  }, [daftarJenis, jenisId]);
  useEffect(() => {
    const urls = foto.map((f) => URL.createObjectURL(f));
    setPreview(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [foto]);

  const pilihFoto = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setPesan("");
    try {
      const hasil = [];
      for (const f of files) hasil.push(await kompresFoto(f));
      setFoto((lama) => [...lama, ...hasil].slice(0, MAKS_FOTO));
    } catch (err) {
      setPesan(err.message);
    }
  };

  const kirim = async (e) => {
    e.preventDefault();
    if (foto.length === 0) {
      setPesan("Foto bukti wajib diunggah minimal satu");
      return;
    }
    setSibuk(true);
    setPesan("");
    try {
      const fd = new FormData();
      fd.append("RoomID", room.RoomID);
      fd.append("JenisID", jenisId);
      fd.append("Tanggal", tanggal);
      fd.append("Catatan", catatan);
      foto.forEach((f) => fd.append("Foto", f));
      await axios.post(API + "insertAkomodasiLayananLog", fd, { headers: headers() });
      setFoto([]);
      setCatatan("");
      await onUbah();
    } catch (err) {
      setPesan(pesanError(err, "Gagal menyimpan layanan"));
    }
    setSibuk(false);
  };

  const hapus = async (log) => {
    if (!window.confirm(`Hapus catatan "${log.NamaJenis}" ini? Foto buktinya ikut tidak ditampilkan lagi.`)) return;
    setSibuk(true);
    try {
      await axios.post(API + "deleteAkomodasiLayananLog", new URLSearchParams({ LogID: log.LogID }), { headers: headers() });
      await onUbah();
    } catch (err) {
      setPesan(pesanError(err, "Gagal menghapus"));
    }
    setSibuk(false);
  };

  const selesai = new Set(room.LayananHariIni.filter((l) => l.Tahap === "SELESAI").map((l) => l.JenisID));
  return (
    <div>
      {room.Layanan.length > 0 && (
        <p className="text-xs text-gray-600 mb-2">
          Paket servis:{" "}
          {room.Layanan.map((id) => {
            const nama = data.JenisLayanan.find((j) => j.JenisID === id)?.Nama || id;
            return (
              <span
                key={id}
                className={"inline-block rounded px-1.5 mr-1 " + (selesai.has(id) ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800")}
              >
                {nama} {selesai.has(id) ? "✓" : "· belum"}
              </span>
            );
          })}
        </p>
      )}

      {room.LayananHariIni.length === 0 ? (
        <p className="text-sm text-gray-500 mb-2">Belum ada layanan tercatat pada {tanggal}.</p>
      ) : (
        <ul className="mb-3 divide-y border rounded">
          {room.LayananHariIni.map((l) => (
            <li key={l.LogID} className="p-2 text-sm">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <div className="font-medium text-gray-800">{l.NamaJenis}</div>
                  <div className="text-xs text-gray-500">
                    {l.Oleh} · {l.Waktu}
                    {l.Catatan ? " · " + l.Catatan : ""}
                  </div>
                </div>
                {data.BolehIsi && (
                  <button type="button" disabled={sibuk} onClick={() => hapus(l)} className="text-xs border border-red-300 text-red-600 rounded px-2 py-0.5">
                    Hapus
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {l.Foto.map((fid) => (
                  <FotoThumb key={fid} id={fid} onBuka={onBukaFoto} />
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}

      {data.BolehIsi && (
        <form onSubmit={kirim} className="border-t pt-3 space-y-2">
          <p className="text-xs font-semibold text-gray-600 uppercase">Catat layanan ({tanggal})</p>
          <select value={jenisId} onChange={(e) => setJenisId(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm w-full">
            {daftarJenis.map((j) => (
              <option key={j.JenisID} value={j.JenisID}>
                {j.Nama}
                {room.Layanan.includes(j.JenisID) ? "" : " (di luar paket)"}
              </option>
            ))}
          </select>
          <input
            value={catatan}
            onChange={(e) => setCatatan(e.target.value)}
            placeholder="Catatan (opsional, wajib bila Lain-lain)"
            className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
            required={jenisId === "LAINNYA"}
          />
          <div className="flex flex-wrap gap-1">
            {preview.map((u, i) => (
              <div key={u} className="relative">
                <img src={u} alt="" className="w-16 h-16 object-cover rounded border border-gray-300" />
                <button
                  type="button"
                  onClick={() => setFoto((f) => f.filter((_, x) => x !== i))}
                  className="absolute -top-1 -right-1 bg-white border border-gray-400 rounded-full w-5 h-5 text-xs leading-none"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <input ref={inputRef} type="file" accept="image/*" capture="environment" multiple onChange={pilihFoto} className="hidden" />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={foto.length >= MAKS_FOTO}
            className="border border-gray-300 rounded px-3 h-9 text-sm w-full disabled:opacity-50"
          >
            {foto.length === 0 ? "Ambil / pilih foto bukti (wajib)" : `Tambah foto (${foto.length}/${MAKS_FOTO})`}
          </button>
          {pesan && <p className="text-sm text-red-600">{pesan}</p>}
          <button type="submit" disabled={sibuk || !jenisId} style={{ backgroundColor: Colors.primary }} className="text-white rounded px-3 h-9 text-sm w-full disabled:opacity-60">
            {sibuk ? "Mengunggah..." : "Simpan Layanan"}
          </button>
        </form>
      )}
    </div>
  );
}

// Grafik per jenis servis pada tanggal terpilih: room berpaket vs room terisi vs room sudah dilayani vs penghuni.
export function GrafikServis({ data, roomTampil, tanggal }) {
  const baris = useMemo(() => {
    return data.JenisLayanan.map((j) => {
      const berpaket = roomTampil.filter((r) => r.Layanan.includes(j.JenisID));
      const terisi = berpaket.filter((r) => r.Penghuni.length > 0);
      const dilayani = berpaket.filter((r) => r.LayananHariIni.some((l) => l.JenisID === j.JenisID && l.Tahap === "SELESAI"));
      return {
        jenis: j.JenisID,
        nama: j.Nama,
        "Room berpaket": berpaket.length,
        "Room terisi": terisi.length,
        "Room sudah dilayani": dilayani.length,
        Penghuni: berpaket.reduce((n, r) => n + r.Penghuni.length, 0),
        belum: berpaket.filter((r) => !dilayani.includes(r)).map((r) => r.NamaRoom),
      };
    }).filter((b) => b["Room berpaket"] > 0);
  }, [data.JenisLayanan, roomTampil]);

  if (baris.length === 0) {
    return (
      <div className="mt-4 border border-gray-200 rounded p-4 text-sm text-gray-500">
        Belum ada paket servis. {data.BolehIsi ? "Klik sebuah room lalu atur paket servisnya, atau pilih banyak room di mode Atur Denah." : "Tim HRGA/GA yang mengisi paket servis."}
      </div>
    );
  }
  return (
    <div className="mt-4 border border-gray-200 rounded p-3 bg-white">
      <h2 className="text-sm font-semibold text-gray-800 mb-2">Servis per jenis — {tanggal}</h2>
      <div style={{ width: "100%", height: 300 }}>
        <ResponsiveContainer>
          <BarChart data={baris} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="nama" tick={{ fontSize: 12 }} />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend />
            <Bar dataKey="Room berpaket" fill="#9ca3af" />
            <Bar dataKey="Room terisi" fill="#3b82f6" />
            <Bar dataKey="Room sudah dilayani" fill="#22c55e" />
            <Bar dataKey="Penghuni" fill="#f59e0b" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 text-xs text-gray-700 space-y-0.5">
        {baris.map((b) => (
          <div key={b.jenis}>
            <span className="font-semibold">{b.nama}:</span>{" "}
            {b.belum.length === 0 ? <span className="text-green-700">semua room sudah dilayani</span> : <span className="text-amber-700">belum dilayani — {b.belum.join(", ")}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
